import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
    Upload,
    CheckCircle,
    X,
    Brain,
} from "lucide-react";
import PhoneInput, {
    isValidPhoneNumber,
    getCountries,
} from "react-phone-number-input";
import CustomDropdown from "../../components/common/CustomDropdown";

const StandardATSForm = ({
    linkToken,
    jobDetails,
    branding,
    isWhiteLabelingEnable,
    host,
    setApplicationSubmitted,
    setApplicationClosed,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isExtracting, setIsExtracting] = useState(false);
    const [extractionProgress, setExtractionProgress] = useState(0);
    const [defaultCountry, setDefaultCountry] = useState(null);

    // Form state - Mandatory fields
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [mobile, setMobile] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [experience, setExperience] = useState("");
    const [noticePeriod, setNoticePeriod] = useState("");

    // Optional fields
    const [sourceOfApplication, setSourceOfApplication] = useState("");
    const [otherSourceDetails, setOtherSourceDetails] = useState("");

    // Resume upload
    const [resume, setResume] = useState(null);
    const [resumeName, setResumeName] = useState("");
    const [isDragOver, setIsDragOver] = useState(false);

    // AI Resume parsing states
    const [autoFilledFields, setAutoFilledFields] = useState([]);

    const experienceOptions = [
        "0-1 years",
        "1-2 years",
        "2-3 years",
        "3-5 years",
        "5-7 years",
        "7-10 years",
        "10+ years",
    ];

    const noticePeriodOptions = [
        "Fresher",
        "Immediate",
        "15 days",
        "30 days",
        "45 days",
        "60 days",
        "90 days",
        "Serving notice period",
    ];

    const sourceOptions = [
        "LinkedIn",
        "Naukri.com",
        "Indeed",
        "Monster.com",
        "Company Website",
        "Employee Referral",
        "Recruiter Contact",
        "Social Media",
        "Other",
    ];

    useEffect(() => {
        const detectCountry = async () => {
            try {
                const { data } = await axios.get("https://ipapi.co/json/", {
                    timeout: 3000,
                });
                const code = data.country_code?.toUpperCase();
                if (code && getCountries().includes(code)) {
                    setDefaultCountry(code);
                }
            } catch (e) {
                console.warn("IP geolocation failed, using fallback");
            }
        };
        detectCountry();
    }, []);

    const validateFile = (file) => {
        const maxSize = 3 * 1024 * 1024; // 3MB
        const allowedTypes = [
            "application/pdf",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/msword",
        ];
        const allowedExtensions = [".pdf", ".docx", ".doc"];

        if (!file || !file.name) {
            return { valid: false, error: "No file provided" };
        }

        if (file.size > maxSize) {
            return { valid: false, error: "File size exceeds 3MB limit" };
        }

        const fileName = file.name.toLowerCase();
        const hasValidExtension = allowedExtensions.some((ext) =>
            fileName.endsWith(ext)
        );

        if (!allowedTypes.includes(file.type) && !hasValidExtension) {
            return {
                valid: false,
                error: "Invalid file type. Only PDF, DOCX, and DOC files are supported",
            };
        }

        return { valid: true, error: null };
    };

    const resetExtractionState = () => {
        setIsExtracting(false);
        setExtractionProgress(0);
        setAutoFilledFields([]);
    };

    const extractResumeData = async (file) => {
        setIsExtracting(true);
        setExtractionProgress(0);

        const progressInterval = setInterval(() => {
            setExtractionProgress((prev) => {
                if (prev >= 90) return prev;
                return prev + 10;
            });
        }, 300);

        try {
            const formData = new FormData();
            formData.append("file", file);
            const response = await axios.post(`${host}/the_parse_resume`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const extractedData = response.data;

            clearInterval(progressInterval);
            setExtractionProgress(100);

            const filledFields = [];

            if (extractedData.name && !name.trim()) {
                setName(extractedData.name);
                filledFields.push("Name");
            }
            if (extractedData.email && !email.trim()) {
                setEmail(extractedData.email);
                filledFields.push("Email");
            }
            if (extractedData.mobile && !mobile.trim()) {
                setMobile(extractedData.mobile);
                filledFields.push("Mobile");
            }
            if (extractedData.currentLocation) {
                if (!city.trim() && !state.trim()) {
                    const [extractedCity, extractedState] =
                        extractedData.currentLocation.split(",");
                    setCity(extractedCity?.trim() || "");
                    setState(extractedState?.trim() || "");
                    filledFields.push("Location");
                }
            }
            if (
                extractedData.experience &&
                !experience &&
                experienceOptions.includes(extractedData.experience)
            ) {
                setExperience(extractedData.experience);
                filledFields.push("Experience");
            }

            setAutoFilledFields(filledFields);
        } catch (error) {
            console.error("Resume extraction failed:", error);
            clearInterval(progressInterval);
            setExtractionProgress(0);
            toast.error(
                error.response?.data?.detail ||
                "Could not extract information from resume."
            );
        } finally {
            setTimeout(() => {
                setIsExtracting(false);
            }, 500);
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) {
            setResume(null);
            setResumeName("");
            resetExtractionState();
            return;
        }

        const validation = validateFile(file);
        if (!validation.valid) {
            toast.error(validation.error);
            setResume(null);
            setResumeName("");
            return;
        }

        setResume(file);
        setResumeName(file.name);
        await extractResumeData(file);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];

        if (file) {
            const validation = validateFile(file);
            if (!validation.valid) {
                toast.error(validation.error);
                return;
            }

            setResume(file);
            setResumeName(file.name);
            await extractResumeData(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const removeFile = () => {
        window.location.reload();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name.trim()) {
            toast.error("Please enter your full name.");
            return;
        }
        if (!email.trim()) {
            toast.error("Please enter your email address.");
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast.error("Please enter a valid email address.");
            return;
        }
        if (!mobile || !isValidPhoneNumber(mobile)) {
            toast.error("Please enter a valid mobile number with country code.");
            return;
        }
        if (!city.trim()) {
            toast.error("Please enter your city.");
            return;
        }
        if (!state.trim()) {
            toast.error("Please enter your state.");
            return;
        }
        if (!experience) {
            toast.error("Please select your years of experience.");
            return;
        }
        if (!noticePeriod) {
            toast.error("Please select your notice period.");
            return;
        }
        if (!resume) {
            toast.error("Please upload your resume.");
            return;
        }
        if (!sourceOfApplication || !sourceOfApplication.trim()) {
            toast.error("Please select how you heard about us.");
            return;
        }
        if (sourceOfApplication === "Other" && !otherSourceDetails.trim()) {
            toast.error(
                "Please specify how you heard about us when 'Other' is selected."
            );
            return;
        }

        setIsSubmitting(true);

        try {
            const formData = new FormData();
            formData.append("name", name.trim());
            formData.append("email", email.trim());
            formData.append("mobile", mobile.trim());
            formData.append("currentLocation", `${city.trim()}, ${state.trim()}`);
            formData.append("experience", experience);
            formData.append("noticePeriod", noticePeriod);
            formData.append("sourceOfApplication", sourceOfApplication.trim());
            formData.append("otherSourceDetails", otherSourceDetails.trim());
            formData.append("resume", resume);

            const response = await axios.post(
                `${host}/public/apply/${linkToken}`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );

            setApplicationSubmitted(true);
            toast.success("Application submitted successfully!");
        } catch (error) {
            console.error("Error submitting application:", error);
            if (error.response) {
                const detail = error.response.data.detail || "Unknown error";
                toast.error(`Error: ${detail}`);
            } else {
                toast.error("An unexpected error occurred. Please try again.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className={`rounded-3xl p-8 shadow-sm ${isWhiteLabelingEnable ? "bg-white backdrop-blur-sm" : "glass-effect"
                }`}
        >
            <h3
                className={`text-xl font-medium mb-6 ${isWhiteLabelingEnable ? "text-gray-700" : "text-[#C1A0D0]"
                    }`}
            >
                Submit Your Application
            </h3>
            <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-4">
                    <h4
                        className={`text-md font-medium border-b pb-2 ${isWhiteLabelingEnable
                            ? "border-gray-300 text-gray-600"
                            : "text-white border-b border-gray-600"
                            }`}
                    >
                        Upload Resume
                    </h4>
                    <div>
                        <div className="relative">
                            <input
                                type="file"
                                accept=".pdf,.docx,.doc"
                                onChange={handleFileChange}
                                className="hidden"
                                id="resumeUpload"
                            />
                            <div
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                className={`border-2 border-dashed rounded-lg cursor-pointer transition-colors ${!isWhiteLabelingEnable
                                    ? "border-[#a88eb4]"
                                    : "border-[#1569c4] hover:border-gray-500"
                                    } ${resumeName ? "border-green-500 bg-green-500/10" : ""}`}
                            >
                                {isExtracting ? (
                                    <div className="p-4">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <Brain className={`w-6 h-6 animate-pulse ${isWhiteLabelingEnable ? 'text-gray-700' : 'text-[#C1A0D0] '}`} />
                                            <p
                                                className={`font-medium ${isWhiteLabelingEnable ? "text-gray-700" : "text-white"
                                                    }`}
                                            >
                                                Extracting data from {resumeName}...
                                            </p>
                                        </div>
                                        <div className="w-full bg-gray-600 rounded-full h-1">
                                            <div
                                                className={`${isWhiteLabelingEnable
                                                    ? "bg-[#1569c4]"
                                                    : "bg-gradient-to-r from-pink-500 to-blue-500"
                                                    } h-1 rounded-full transition-all duration-300`}
                                                style={{ width: `${extractionProgress}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                ) : resumeName ? (
                                    <div className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <CheckCircle className="w-6 h-6 text-green-400" />
                                                <div>
                                                    <p
                                                        className={`font-medium ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"
                                                            }`}
                                                    >
                                                        {resumeName}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={removeFile}
                                                className="p-1 rounded-full hover:bg-gray-500/30 transition-colors"
                                            >
                                                <X
                                                    className={`w-4 h-4 ${isWhiteLabelingEnable ? "text-gray-500" : "text-white"
                                                        }`}
                                                />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <label
                                        htmlFor="resumeUpload"
                                        className="block p-8 text-center cursor-pointer"
                                    >
                                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                                        <p
                                            className={`mb-1 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"
                                                }`}
                                        >
                                            Drag and drop your resume here
                                        </p>
                                        <p className="text-sm text-gray-300 mb-2">or</p>
                                        <span
                                            className={`inline-block px-4 py-2 ${isWhiteLabelingEnable
                                                ? "bg-[#1569c4] hover:bg-[#125eaf] text-white"
                                                : "bg-[#C1A0D0]/20 text-[#C1A0D0] hover:bg-[#C1A0D0]/30"
                                                } rounded-lg text-sm font-medium transition-colors hover:scale-105`}
                                        >
                                            Browse Files
                                        </span>
                                    </label>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <h4
                        className={`text-md font-medium ${isWhiteLabelingEnable
                            ? "text-gray-700 border-b border-gray-400"
                            : "text-white border-b border-gray-600"
                            } pb-2`}
                    >
                        Personal Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label
                                className={`block text-sm font-medium ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"
                                    } mb-2`}
                            >
                                Full Name *
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className={`w-full px-4 py-3 bg-transparent border border-gray-400 rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"
                                    }`}
                                placeholder="Enter your full name"
                                required
                            />
                        </div>
                        <div>
                            <label
                                className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"
                                    }`}
                            >
                                Email Address *
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={`w-full px-4 py-3 bg-transparent border border-gray-400 rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"
                                    }`}
                                placeholder="your.email@example.com"
                                required
                            />
                        </div>
                        <div>
                            <label
                                className={`block text-sm font-medium ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"
                                    } mb-2`}
                            >
                                Mobile Number *
                            </label>
                            <div className="w-full">
                                <PhoneInput
                                    international
                                    defaultCountry={defaultCountry}
                                    value={mobile}
                                    className={`w-full px-4 py-0 bg-transparent border ${mobile ? isWhiteLabelingEnable ? "!text-textSecondary" :"!text-[#bdbdbd]" : isWhiteLabelingEnable ? "!text-gray-400" :"!text-[#bdbdbd]" } rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors`}
                                    onChange={(value) => setMobile(value || "")}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label
                                className={`block text-sm font-medium ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"
                                    } mb-2`}
                            >
                                City *
                            </label>
                            <input
                                type="text"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                className={`w-full px-4 py-3 bg-transparent border border-gray-400 rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"
                                    }`}
                                placeholder="Enter your city"
                                required
                            />
                        </div>
                        <div>
                            <label
                                className={`block text-sm font-medium ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"
                                    } mb-2`}
                            >
                                State *
                            </label>
                            <input
                                type="text"
                                value={state}
                                onChange={(e) => setState(e.target.value)}
                                className={`w-full px-4 py-3 bg-transparent border border-gray-400 rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"
                                    }`}
                                placeholder="Enter your state"
                                required
                            />
                        </div>
                        <div>
                            <label
                                className={`block text-sm font-medium ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"
                                    } mb-2`}
                            >
                                Years of Experience *
                            </label>
                            <CustomDropdown
                                options={experienceOptions}
                                value={experience}
                                onChange={(e) => setExperience(e)}
                                placeholder="Select experience level"
                                required
                                isWhiteLabelingEnable={isWhiteLabelingEnable}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label
                                className={`block text-sm font-medium ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"
                                    } mb-2`}
                            >
                                Notice Period *
                            </label>
                            <CustomDropdown
                                options={noticePeriodOptions}
                                value={noticePeriod}
                                onChange={(e) => setNoticePeriod(e)}
                                placeholder="Select notice period"
                                required
                                isWhiteLabelingEnable={isWhiteLabelingEnable}
                            />
                        </div>
                        <div>
                            <label
                                className={`block text-sm font-medium ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"
                                    } mb-2`}
                            >
                                How did you hear about us? *
                            </label>
                            <CustomDropdown
                                options={sourceOptions}
                                value={sourceOfApplication}
                                onChange={(e) => setSourceOfApplication(e)}
                                placeholder="Select Source"
                                required
                                isWhiteLabelingEnable={isWhiteLabelingEnable}
                            />
                            {sourceOfApplication === "Other" && (
                                <input
                                    type="text"
                                    value={otherSourceDetails}
                                    onChange={(e) => setOtherSourceDetails(e.target.value)}
                                    className={`w-full mt-3 px-4 py-3 bg-transparent border border-gray-400 rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                    placeholder="Please specify"
                                    maxLength={200}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className="w-full h-fit flex items-center justify-center pt-4">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-[35%] py-3 rounded-lg font-medium transition-all ${isSubmitting
                            ? "bg-[#1569c4]/30 text-gray-400 cursor-not-allowed"
                            : isWhiteLabelingEnable
                                ? "bg-[#1569c4] text-white hover:scale-105 transition-all"
                                : "bg-gradient-to-r from-pink-500 to-blue-500 text-white hover:opacity-90"
                            }`}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="sm:hidden">Submitting...</span>
                                <span className="hidden sm:inline">Submitting...</span>
                            </>
                        ) : (
                            <>
                                <span className="sm:hidden">Submit</span>
                                <span className="hidden sm:inline">Submit Application</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default StandardATSForm;
